import { Link } from "react-router-dom";
import type { PrequalInviteResolution } from "../../api/prequalification";

/** Matches the funnel's card idiom (the same shell PrequalResultPage and
 *  DealExplorerPage use). Kept local rather than exported from PrequalBits so
 *  this file has no dependency on the funnel's step machinery. */
const CARD_SHELL =
  "rounded-[14px] border border-line bg-white-pure shadow-[0_10px_34px_rgba(20,20,20,0.07)]";

/**
 * What a dead invite link shows.
 *
 * A locked product decision: an expired, revoked, already-used, or unknown
 * link is a FRIENDLY dead end, never an error page. Somebody was invited here
 * by a person they know; the useful thing is to tell them who to ask, not to
 * show them a stack of jargon or a 404.
 *
 * The four dead states get slightly different copy because they mean genuinely
 * different things to the reader — "you already did this" and "your link aged
 * out" want different next actions — but every one of them ends at the same
 * place: the still-open public funnel, so a determined visitor is never fully
 * blocked by a stale URL.
 *
 * Note what is NOT rendered: the recipient's name or email. The server
 * withholds them for a dead invite, and a stale link in the wrong hands must
 * not read contact details back out.
 */
export function PrequalInviteExpiredCard({
  invite,
  role,
}: {
  invite: PrequalInviteResolution;
  role: "Buyer" | "Seller";
}) {
  const sender = invite.invitedByLabel?.trim();
  const senderPhrase = sender && sender.length > 0 ? sender : "whoever sent it to you";

  // An unknown token is deliberately indistinguishable from an expired one —
  // the server shapes them identically so a prober learns nothing — so this
  // switch never needs a "not found" arm.
  const { heading, body } = copyFor(invite.status, senderPhrase);

  // Keep the visitor on their partner's site when we know it, so the fallback
  // link does not silently move them to the Phoenix-native funnel.
  const freshStart = invite.partnerSlug
    ? `/prequalify/${invite.partnerSlug}/${role === "Buyer" ? "buyer" : "seller"}`
    : `/prequalify/${role === "Buyer" ? "buyer" : "seller"}`;

  return (
    <section
      className={`${CARD_SHELL} p-space-4 sm:p-space-6 max-w-2xl mx-auto`}
      data-testid="prequal-invite-expired"
      aria-live="polite"
    >
      <p className="text-label uppercase tracking-widest sm:tracking-[0.24em] text-gold-deep mt-0 mb-space-2">
        Invitation link
      </p>
      <h1 className="font-semibold normal-case tracking-normal text-[1.45rem] leading-tight text-ink mt-0 mb-space-3">
        {heading}
      </h1>
      <p className="text-small text-ink-mid m-0">{body}</p>
      <p className="text-small text-ink-mid mt-space-4 mb-0">
        You can also{" "}
        <Link to={freshStart} className="text-gold-deep underline">
          start a prequalification on your own
        </Link>
        {" "}— it asks the same questions and takes a few minutes.
      </p>
    </section>
  );
}

function copyFor(status: string, senderPhrase: string): { heading: string; body: string } {
  switch (status) {
    case "Completed":
      return {
        heading: "This invitation has already been used",
        body:
          `A prequalification was already completed from this link. If you need another copy of `
          + `the results, ask ${senderPhrase} — they can see it on their side.`,
      };
    case "Revoked":
      return {
        heading: "This invitation was withdrawn",
        body:
          `${senderPhrase} withdrew this invitation. If you think that was a mistake, get in `
          + `touch with them and they can send a fresh one.`,
      };
    case "ClosedBySibling":
      return {
        heading: "You have already prequalified",
        body:
          `A prequalification for you was completed through a different link, so this one is `
          + `no longer needed. If you need another copy of the results, ask ${senderPhrase}.`,
      };
    case "Unavailable":
      return {
        heading: "This invitation link is no longer working",
        // DELIBERATELY says nothing about WHY. This branch is reached when the
        // sending broker's record has been off-ramped, and the reader may be
        // the buyer, someone they forwarded the link to, or anyone who found
        // it — none of whom should learn that a named broker is in trouble
        // with Phoenix. The server withholds the reason for the same reason it
        // withholds the recipient's contact details, and the status string it
        // sends is itself deliberately vague.
        //
        // It also does not tell them to go back to the sender: that is the one
        // person who cannot help here. The public funnel below is the whole
        // next step, and this branch leans on it harder than the others do.
        body:
          `Something about this link is out of date, so it can no longer be used. Nothing you `
          + `did caused it, and your details were not lost — you can pick up where you would `
          + `have started, below.`,
      };
    default:
      return {
        heading: "This invitation link has expired",
        body:
          `Invitation links stay good for 60 days. Ask ${senderPhrase} to resend yours — it `
          + `takes them a moment and your new link will work straight away.`,
      };
  }
}
